import React, {useState} from 'react';
import {Box, Button, Chip, Fade, TextField} from '@mui/material';
import {WelcomeScreen} from 'crqlar-react-common/dist/models/forms/form';
import EditableTypography from 'crqlar-react-common/dist/components/EditableTypography';
import VideocamIcon from '@mui/icons-material/Videocam';
import MdEditor from 'crqlar-react-common/dist/components/MdEditor';
import {useTranslation} from 'react-i18next';

interface WelcomeScreenEditorProps {
    question: WelcomeScreen;
    onQuestionUpdate: (question: WelcomeScreen) => void;
}

const WelcomeScreenEditor: React.FC<WelcomeScreenEditorProps> = ({
                                                                     question,
                                                                     onQuestionUpdate
                                                                 }) => {
    const {t} = useTranslation();
    const [isEditingButton, setIsEditingButton] = useState(false);

    // Handle title update
    const handleTitleUpdate = (value: string) => {
        onQuestionUpdate({
            ...question,
            title: value,
        });
    };

    // Handle description update
    const handleDescriptionUpdate = (value?: string) => {
        onQuestionUpdate({
            ...question,
            description: value || ''
        });
    };

    // Handle button text update
    const handleButtonTextUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            buttonText: e.target.value,
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                p: 2,
            }}
        >
            {/* Video Background Indicator */}
            {question.videoMedia && (
                <Chip
                    icon={<VideocamIcon />}
                    label={t('Video Background Active')}
                    color="primary"
                    size="small"
                    sx={{
                        mb: 2,
                        fontWeight: 500
                    }}
                />
            )}

            {/* Title */}
            <EditableTypography
                value={question.title}
                onChange={handleTitleUpdate}
                multiline
                variant="h3"
                sx={{
                    mb: 2,
                    width: '100%',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    '& input': {
                        textAlign: 'center',
                    },
                    padding: '8px',
                    borderRadius: 1,
                }}
            />

            {/* Description */}
            <Box sx={{mb: 4, width: '100%'}}>
                <MdEditor
                    value={question.description || ''}
                    onChange={handleDescriptionUpdate}
                    placeholder={t('Welcome message (optional)')}
                    className={'mini-mdx'}
                />
            </Box>

            {/* Start Button */}
            <Box
                sx={{
                    position: 'relative',
                    display: 'inline-block',
                }}
                onMouseEnter={() => setIsEditingButton(true)}
                onMouseLeave={() => setIsEditingButton(false)}
            >
                <Button
                    variant="contained"
                    color="primary"
                    sx={{
                        textTransform: 'none',
                        fontSize: '1rem',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                    }}
                >
                    {question.buttonText || t('Start')}
                </Button>

                <Fade in={isEditingButton}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            mt: 1,
                            zIndex: 10,
                            width: 200,
                        }}
                    >
                        <TextField
                            value={question.buttonText || ''}
                            onChange={handleButtonTextUpdate}
                            variant="outlined"
                            size="small"
                            placeholder={t('Button text')}
                            fullWidth
                            autoFocus
                            sx={{
                                backgroundColor: 'background.paper',
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1,
                                }
                            }}
                        />
                    </Box>
                </Fade>
            </Box>
        </Box>
    );
};

export default WelcomeScreenEditor;
